/**
 * Product Generation API Service
 *
 * This service handles AI-powered product generation by calling the backend API endpoint.
 * It follows the same patterns as other services in the project, extending the Api base class
 * for automatic authentication and error handling.
 * 
 * Data Sources: Journey Foods Database + Claude AI
 * - Product data is sourced from Journey Foods comprehensive ingredient and product database
 * - Claude AI processes the user description and generates product specifications
 * - Company information is pulled from Firebase/Journey Foods user database
 */

import { Api } from "./api";
import config from "../utils/config";

interface GenerateProductRequest {
	description: string;
	model?: "claude" | "gemini" | "journey_ai";
}

interface GenerateProductResponse {
	success: boolean;
	product?: any;
	error?: string;
}

/**
 * Formats the generated product data to match the backend API expectations
 * This ensures compatibility with the addProduct endpoint
 */
/**
 * Calculate cost margin from cost and markup
 */
function getCostMarkup(cost: number, markup: number): number {
	if (cost === 0) {
		return 0;
	}
	return Math.round((cost * markup) / 100);
}

function formatGeneratedProductForBackend(productData: any): any {
	// Start with a copy of all original data to preserve all fields
	const formatted: any = { ...productData };

	// Calculate cost_margin from cost and markup if not provided
	const cost = productData.cost ? Number(productData.cost) : 0;
	const markup = productData.markup ? Number(productData.markup) : 0;
	formatted.cost = cost;
	formatted.markup = markup;
	formatted.cost_margin =
		productData.cost_margin !== undefined
			? Number(productData.cost_margin)
			: getCostMarkup(cost, markup);

	// Ensure required fields have defaults if missing
	formatted.name = productData.name || "";
	formatted.code = productData.code || "";
	formatted.flavor = productData.flavor || "";
	formatted.product_class = productData.product_class || "";
	formatted.product_status =
		productData.product_status !== undefined ? productData.product_status : false;
	formatted.serving_size =
		productData.serving_size !== undefined ? Number(productData.serving_size) : 0;
	formatted.unit_serving_size = productData.unit_serving_size || "oz";
	formatted.notes = productData.notes || "";
	formatted.version = productData.version || 1;
	formatted.ingredients = Array.isArray(productData.ingredients) ? productData.ingredients : [];
	formatted.new_ingredients = productData.new_ingredients || []; // Preserve if exists, otherwise empty array
	formatted.newly_added_product_ingredient =
		productData.newly_added_product_ingredient !== undefined
			? productData.newly_added_product_ingredient
			: null;
	formatted.packaging = productData.packaging || null;
	formatted.created_date = productData.created_date || new Date().toISOString();
	formatted.fulfillment_date = productData.fulfillment_date || new Date().toISOString();
	formatted.additives =
		productData.additives !== undefined ? Boolean(productData.additives) : false;
	formatted.is_guava = productData.is_guava !== undefined ? Boolean(productData.is_guava) : false;
	formatted.is_ai_generated =
		productData.is_ai_generated !== undefined ? productData.is_ai_generated : true; // Mark products generated by Journey AI

	// Add guava_score if provided
	if (productData.guava_score !== undefined && productData.guava_score !== null) {
		formatted.guava_score = Number(productData.guava_score);
	}

	// Format objective - should be an array of strings
	if (productData.objective) {
		if (Array.isArray(productData.objective)) {
			formatted.objective = productData.objective.map((obj: any) =>
				typeof obj === "string" ? obj : obj.value || obj.label || obj
			);
		} else if (typeof productData.objective === "string") {
			formatted.objective = [productData.objective];
		}
	}

	// Format brand - preserve full object if it exists, otherwise format for backend
	if (productData.brand !== undefined && productData.brand !== null) {
		if (typeof productData.brand === "string") {
			// Empty string should be null
			formatted.brand = productData.brand.trim() ? { id: productData.brand.trim() } : null;
		} else if (productData.brand.id) {
			// Preserve full brand object if it exists, but ensure id is available for backend
			formatted.brand = productData.brand;
		} else if (productData.brand.name) {
			// If only name exists, set to null (backend needs ID)
			formatted.brand = null;
		}
		// If brand is already a full object, preserve it as is
	} else {
		formatted.brand = null;
	}

	// Format company - preserve full object if it exists
	if (productData.company) {
		if (typeof productData.company === "string") {
			formatted.company = productData.company;
		} else if (productData.company.id) {
			// Preserve full company object if it exists
			formatted.company = productData.company;
		}
		// If company is already a full object, preserve it as is
	}

	// Format currency - preserve full object if it exists
	if (productData.currency) {
		if (typeof productData.currency === "string") {
			formatted.currency = productData.currency;
		} else if (productData.currency.id) {
			// Preserve full currency object if it exists
			formatted.currency = productData.currency;
		}
	}

	// Format country - preserve full object if it exists
	if (productData.country) {
		if (typeof productData.country === "string") {
			formatted.country = productData.country;
		} else if (productData.country.id) {
			// Preserve full country object if it exists
			formatted.country = productData.country;
		}
	}

	// Format manufacturer - preserve full object if it exists
	if (productData.manufacturer) {
		if (typeof productData.manufacturer === "string") {
			formatted.manufacturer = productData.manufacturer;
		} else if (productData.manufacturer.id) {
			// Preserve full manufacturer object if it exists
			formatted.manufacturer = productData.manufacturer;
		} else if (productData.manufacturer.name) {
			formatted.manufacturer = productData.manufacturer;
		}
	}

	// Format product_type - preserve full object if it exists
	if (productData.product_type) {
		if (typeof productData.product_type === "object" && productData.product_type.id) {
			// Preserve full product_type object
			formatted.product_type = {
				title: productData.product_type.title || "",
				category: productData.product_type.category || "",
				id: productData.product_type.id,
				sub_category: productData.product_type.sub_category || ""
			};
		}
		// If product_type is already in correct format, it's already preserved from spread
	}

	// Ensure brand is null if it's an empty string
	if (
		formatted.brand === "" ||
		(typeof formatted.brand === "string" && formatted.brand.trim() === "")
	) {
		formatted.brand = null;
	}

	return formatted;
}

/**
 * Product Generation Service
 * Extends the Api base class to leverage automatic authentication and error handling
 */
class ProductGenerationService extends Api {
	/**
	 * Generate a product using AI based on a description/query
	 *
	 * @param request - The product generation request containing the description
	 * @returns Promise resolving to a GenerateProductResponse
	 */
	generateProduct = async (request: GenerateProductRequest): Promise<GenerateProductResponse> => {
		try {
			// Validate input
			if (
				!request.description ||
				typeof request.description !== "string" ||
				!request.description.trim()
			) {
				return {
					success: false,
					error: "Product description is required"
				};
			}

			// Map description to query as expected by the API
			const requestBody = {
				query: request.description.trim(),
				model: request.model || "journey_ai"
			};

			// Make API call using the http instance from Api base class
			// This automatically includes authentication headers
			const response = await this.http.post(
				config.api.endpoints.products.createProduct,
				requestBody
			);

			// Extract product data from response
			// The API may return the product directly or wrapped in a data property
			const rawProductData = response.data?.product || response.data?.data || response.data;

			if (!rawProductData) {
				return {
					success: false,
					error: "Invalid response format from server"
				};
			}

			// Format the product data to ensure compatibility with backend expectations
			// This function now preserves all fields from the backend response
			const formattedProductData = formatGeneratedProductForBackend(rawProductData);

			// All fields including id, created_by, updated_by, etc. are now preserved
			// The formatGeneratedProductForBackend function preserves all fields from the response

			return {
				success: true,
				product: formattedProductData
			};
		} catch (error) {
			console.error("Error in generateProduct:", error);

			// Handle error response from API
			const axiosError = error as any;
			if (axiosError.response) {
				const errorMessage =
					axiosError.response.data?.message ||
					axiosError.response.data?.error ||
					axiosError.response.data?.detail ||
					"Failed to generate product";

				return {
					success: false,
					error: errorMessage
				};
			}

			// Handle network or other errors
			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: "Failed to generate product. Please check your connection and try again."
			};
		}
	}
}

// Create a singleton instance of the service
const productGenerationService = new ProductGenerationService();

/**
 * Generate product API function
 *
 * This is the main export that maintains backward compatibility with existing code.
 * It wraps the service method and provides a clean interface.
 *
 * @param request - The product generation request
 * @returns Promise resolving to a GenerateProductResponse
 */
export const generateProductAPI = async (
	request: GenerateProductRequest
): Promise<GenerateProductResponse> => {
	return productGenerationService.generateProduct(request);
};
